import { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  MapPin,
  Phone,
  Facebook,
  Instagram,
  Youtube,
  CheckCircle2,
  Send,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { SITE } from "@/constants/site";
import { submitForm, errMessage } from "@/lib/api";

const socialIcons = [Facebook, Instagram, Youtube];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!form.name.trim() || !emailOk || !form.message.trim()) {
      toast.error("Please fill in your name, a valid email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitForm("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Thanks for reaching out!", {
        description: "Our team will get back to you soon.",
      });
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="hero-wash">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-halalgo-green-200 bg-halalgo-green-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-halalgo-green-800">
            <Mail className="h-3.5 w-3.5" /> Contact us
          </span>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-halalgo-ink sm:text-5xl">
            Let&apos;s Connect.
          </h1>
          <p className="mt-4 max-w-2xl text-halalgo-ink/70">
            Whether you are a customer needing support, a restaurant looking to
            partner, or a rider ready to hit the road, our team is here for you.
          </p>
        </div>
      </section>

      {/* Info + form */}
      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-5">
          {/* Info column */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {[
                { icon: Mail, label: "Email", value: SITE.email },
                { icon: Phone, label: "Phone", value: SITE.phone },
                { icon: MapPin, label: "Location", value: SITE.location },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-3 rounded-lg border border-halalgo-kraft-100 bg-white p-4 shadow-lift-sm"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-halalgo-green-50 text-halalgo-green-700">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-halalgo-ink">{item.label}</p>
                    <p className="text-sm text-halalgo-ink/70">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-widest text-halalgo-kraft-700">
                Follow us
              </p>
              <div className="mt-3 flex items-center gap-3">
                {socialIcons.map((Icon, i) => (
                  <a
                    key={i}
                    href="#"
                    aria-label="Social media"
                    data-testid={`contact-social-${i}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-halalgo-kraft-200 bg-white text-halalgo-ink/70 transition-colors hover:border-halalgo-green-300 hover:text-halalgo-green-700"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="rounded-2xl border border-halalgo-kraft-100 bg-white p-6 shadow-lift-sm sm:p-8"
            >
              {sent ? (
                <div
                  data-testid="contact-success-message"
                  className="flex flex-col items-center justify-center py-10 text-center"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-halalgo-green-50 text-halalgo-green-700">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-halalgo-ink">
                    Message received
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-halalgo-ink/70">
                    Salamat, {form.name || "friend"}! We&apos;ll be in touch soon
                    {form.role ? ` about your ${form.role.toLowerCase()} enquiry` : ""}.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6 border-halalgo-kraft-200"
                    data-testid="contact-reset-button"
                    onClick={() => {
                      setForm({ name: "", email: "", role: "", message: "" });
                      setSent(false);
                    }}
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" noValidate>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Name</Label>
                      <Input
                        id="contact-name"
                        data-testid="contact-name-input"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Juan Dela Cruz"
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Email</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@email.com"
                        className="h-11"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-role">I am a:</Label>
                    <Select
                      value={form.role}
                      onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
                    >
                      <SelectTrigger
                        id="contact-role"
                        data-testid="contact-role-select"
                        className="h-11"
                      >
                        <SelectValue placeholder="Select one..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Customer" data-testid="contact-role-customer">
                          Customer
                        </SelectItem>
                        <SelectItem value="Restaurant" data-testid="contact-role-restaurant">
                          Restaurant
                        </SelectItem>
                        <SelectItem value="Rider" data-testid="contact-role-rider">
                          Rider
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message</Label>
                    <Textarea
                      id="contact-message"
                      data-testid="contact-message-textarea"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="How can we help?"
                      rows={6}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="h-11 w-full bg-halalgo-green-600 text-white shadow-lift-sm hover:bg-halalgo-green-700 active:scale-[0.98] sm:w-auto sm:px-6"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}{" "}
                    Send Message
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom banner */}
      <section className="bg-halalgo-green-800 text-white" data-testid="contact-banner">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-bold uppercase tracking-[0.25em] sm:text-base">
            SAFE • TRUSTED • CONVENIENT • HALAL
          </p>
        </div>
      </section>
    </div>
  );
}
